<template>
  <div class="flex h-screen">
    <!-- Side Panel -->
    <div
      class="w-64 h-screen flex flex-col bg-gray-100 dark:bg-gray-900 border-r border-gray-300 dark:border-gray-700 transition-colors duration-200"
    >
      <div class="p-4 border-b border-gray-300 dark:border-gray-700 bg-transparent">
        <router-link
          to="/"
          class="flex items-center justify-center cursor-pointer p-1 logo-link"
        >
          <div class="flex items-center">
            <!-- Wrench icon -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-9 w-9 mr-1"
              viewBox="0 0 24 24"
            >
              <path
                d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"
                fill="#FFD700"
                stroke="#FFD700"
                stroke-width="0.5"
              />
            </svg>

            <!-- Text logo -->
            <div class="flex items-center">
              <span class="text-3xl font-bold text-blue-500">dev</span>
              <span class="text-3xl font-bold text-green-500">tools</span>
            </div>
          </div>
        </router-link>
      </div>

      <nav class="mt-4 flex-1 overflow-y-auto">
        <div class="flex flex-col">
          <template
            v-for="item in menuItems"
            :key="item.name"
          >
            <!-- Folder -->
            <template v-if="(item as FolderMenuItem).type === 'folder'">
              <div class="flex flex-col">
                <div
                  class="flex items-center px-4 py-3 text-gray-600 dark:text-gray-300 font-medium"
                >
                  <component
                    :is="item.icon"
                    class="w-5 h-5 mr-3"
                  />
                  <span>{{ item.name }}</span>
                </div>
                <!-- Folder children: leaf links, sub-folders, and dividers -->
                <template
                  v-for="child in (item as FolderMenuItem).children"
                  :key="(child as RegularMenuItem).path || child.name"
                >
                  <!-- Divider: section label inside the folder -->
                  <div
                    v-if="(child as DividerMenuItem).type === 'divider'"
                    class="px-8 pt-3 pb-1 text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 select-none"
                  >
                    {{ child.name }}
                  </div>
                  <!-- Sub-folder: collapsible accordion header + indented children -->
                  <template v-else-if="(child as SubFolderMenuItem).type === 'subfolder'">
                    <button
                      type="button"
                      class="w-full flex items-center px-8 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-800 font-medium"
                      :class="{
                        'bg-gray-200 dark:bg-gray-800': isActive((child as SubFolderMenuItem).path)
                      }"
                      :aria-expanded="isSubfolderExpanded(child as SubFolderMenuItem)"
                      :aria-controls="`subfolder-${(child as SubFolderMenuItem).path}`"
                      @click="toggleSubfolder(child as SubFolderMenuItem)"
                    >
                      <span class="relative shrink-0 mr-3 flex">
                        <component
                          :is="(child as SubFolderMenuItem).icon"
                          class="w-5 h-5"
                        />
                        <span
                          v-if="(child as SubFolderMenuItem).beta"
                          class="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 ring-1 ring-white dark:ring-gray-900"
                          title="Beta"
                          aria-label="Beta"
                        />
                      </span>
                      <span class="flex-1 min-w-0 truncate text-left">{{ child.name }}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-4 h-4 ml-1 shrink-0 transition-transform duration-150"
                        :class="{
                          'rotate-90': isSubfolderExpanded(child as SubFolderMenuItem)
                        }"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                    <div
                      v-show="isSubfolderExpanded(child as SubFolderMenuItem)"
                      :id="`subfolder-${(child as SubFolderMenuItem).path}`"
                    >
                      <router-link
                        v-for="grand in (child as SubFolderMenuItem).children"
                        :key="grand.path"
                        :to="grand.path"
                        class="flex items-center pl-14 pr-4 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800"
                        :class="{
                          'bg-gray-200 dark:bg-gray-800 font-medium text-gray-800 dark:text-gray-100':
                            route.path === grand.path
                        }"
                      >
                        <span class="flex-1 min-w-0 truncate">{{ grand.name }}</span>
                      </router-link>
                    </div>
                  </template>
                  <!-- Regular leaf -->
                  <router-link
                    v-else
                    :to="(child as RegularMenuItem).path"
                    class="flex items-center px-8 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800"
                    :class="{
                      'bg-gray-200 dark:bg-gray-800 font-medium': isActive(
                        (child as RegularMenuItem).path
                      )
                    }"
                  >
                    <span class="relative shrink-0 mr-3 flex">
                      <component
                        :is="(child as RegularMenuItem).icon"
                        class="w-5 h-5"
                      />
                      <span
                        v-if="(child as RegularMenuItem).beta"
                        class="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 ring-1 ring-white dark:ring-gray-900"
                        title="Beta"
                        aria-label="Beta"
                      />
                    </span>
                    <span class="flex-1 min-w-0 truncate">{{ child.name }}</span>
                  </router-link>
                </template>
              </div>
            </template>
            <!-- Regular Menu Item -->
            <router-link
              v-else
              :to="(item as RegularMenuItem).path"
              class="flex items-center px-4 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800"
              :class="{
                'bg-gray-200 dark:bg-gray-800 font-medium': isActive((item as RegularMenuItem).path)
              }"
            >
              <span class="relative shrink-0 mr-3 flex">
                <component
                  :is="item.icon"
                  class="w-5 h-5"
                />
                <span
                  v-if="item.beta"
                  class="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 ring-1 ring-white dark:ring-gray-900"
                  title="Beta"
                  aria-label="Beta"
                />
              </span>
              <span class="flex-1 min-w-0 truncate">{{ item.name }}</span>
            </router-link>
          </template>
        </div>
      </nav>

      <!-- User Info and Logout -->
      <div class="flex-shrink-0 p-4 border-t border-gray-300 dark:border-gray-700">
        <div
          v-if="user"
          class="flex items-center mb-3"
        >
          <!-- Simple colored circle with initials -->
          <div
            class="w-8 h-8 rounded-full flex items-center justify-center mr-2"
            :style="{
              backgroundColor: getUserColor,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.875rem'
            }"
          >
            {{ getUserInitials }}
          </div>
          <div class="flex flex-col">
            <span class="text-sm font-medium text-gray-800 dark:text-white">{{ user.name }}</span>
            <span class="text-xs text-gray-600 dark:text-gray-400 max-w-[180px] truncate">{{
              user.email
            }}</span>
          </div>
        </div>

        <!-- Theme toggle -->
        <div class="flex items-center justify-between mb-3 px-1">
          <span class="text-sm text-gray-600 dark:text-gray-400">Theme</span>
          <button
            class="flex items-center justify-center rounded-full w-10 h-5 bg-gray-300 dark:bg-gray-600 relative"
            @click="theme.toggleTheme"
          >
            <!-- Sun icon for dark mode (switch to light) -->
            <svg
              v-if="theme.isDark.value"
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-yellow-300 absolute right-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <!-- Moon icon for light mode (switch to dark) -->
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 text-blue-400 absolute left-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
            <div
              class="w-4 h-4 rounded-full absolute transition-all duration-300 transform bg-gray-200"
              :class="theme.isDark.value ? 'translate-x-2' : '-translate-x-2'"
            ></div>
          </button>
        </div>

        <button
          class="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rose-500 dark:bg-red-600 hover:bg-rose-600 dark:hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500 dark:focus:ring-red-500 transition-colors duration-200"
          @click="handleLogout"
        >
          <LogoutIcon class="w-4 h-4 mr-2" />
          Logout
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto bg-white dark:bg-gray-800">
      <router-view></router-view>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, inject, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  LogOut01Icon,
  FilterLinesIcon,
  Settings01Icon,
  FolderIcon,
  PackageIcon,
  File01Icon,
  ActivityIcon,
  CheckCircleIcon,
  UsersUpIcon,
  PresentationChart03Icon,
  Database01Icon,
  Server01Icon
} from '@gohighlevel/ghl-icons/24/outline'
import { defineComponent as _dc, h as _h } from 'vue'

const AiQueryIcon = _dc({
  name: 'AiQueryIcon',
  render() {
    return _h(
      'svg',
      {
        xmlns: 'http://www.w3.org/2000/svg',
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        class: this.$attrs.class
      },
      [
        _h('polyline', { points: '7 5 2 12 7 19' }),
        _h('polyline', { points: '17 5 22 12 17 19' }),
        _h('path', {
          d: 'M12 7.5c0 2.2-1.8 4-4 4 2.2 0 4 1.8 4 4 0-2.2 1.8-4 4-4-2.2 0-4-1.8-4-4z',
          fill: 'currentColor',
          stroke: 'none'
        })
      ]
    )
  }
})
import auth from '../../services/auth/auth'
import { useTheme } from '../../utils/useTheme'
import { isAdmin } from '../../utils/adminUtils'

interface MenuItem {
  name: string
  icon?: string
  adminOnly?: boolean
  beta?: boolean
}

interface RegularMenuItem extends MenuItem {
  type?: 'item'
  path: string
}

interface SubFolderMenuItem extends MenuItem {
  type: 'subfolder'
  path: string
  children: RegularMenuItem[]
}

interface DividerMenuItem {
  type: 'divider'
  name: string
  adminOnly?: boolean
}

interface FolderMenuItem extends MenuItem {
  type: 'folder'
  children: (RegularMenuItem | SubFolderMenuItem | DividerMenuItem)[]
}

type FolderChild = RegularMenuItem | SubFolderMenuItem | DividerMenuItem
type MenuItems = (RegularMenuItem | FolderMenuItem)[]

export default defineComponent({
  name: 'SidePanel',
  components: {
    LogoutIcon: LogOut01Icon,
    FilterIcon: FilterLinesIcon,
    SettingsIcon: Settings01Icon,
    FolderIcon,
    PackageIcon,
    FileIcon: File01Icon,
    ActivityIcon,
    CheckCircleIcon,
    UsersIcon: UsersUpIcon,
    ReportIcon: PresentationChart03Icon,
    DatabaseIcon: Database01Icon,
    ServerIcon: Server01Icon,
    AiQueryIcon
  },
  setup() {
    const route = useRoute()
    const theme = inject('theme', useTheme())

    // ── Sidebar subfolder accordion state ──────────────────────────────────
    // Subfolders default to COLLAPSED on every fresh load. The user's manual
    // expand/collapse choices persist within the session via localStorage so
    // navigating between sibling routes inside an open subfolder doesn't make
    // it slam shut, but we no longer auto-expand based on the active route —
    // an active leaf inside a collapsed subfolder still shows the parent
    // header highlighted (via isActive), and the user can click to expand.
    // v2 key: previous behavior auto-expanded subfolders on route entry, which
    // wrote paths into the v1 key without explicit user intent. Bumping the
    // namespace ensures every user starts with collapsed subfolders under the
    // new default-collapsed contract regardless of stale v1 state.
    const SIDEBAR_EXPANDED_KEY = 'sidePanel.subfolderExpanded.v2'
    function readSet(key: string): Set<string> {
      try {
        if (typeof localStorage === 'undefined') return new Set()
        const raw = localStorage.getItem(key)
        if (!raw) return new Set()
        const arr = JSON.parse(raw)
        return Array.isArray(arr) ? new Set(arr.filter((s) => typeof s === 'string')) : new Set()
      } catch {
        return new Set()
      }
    }
    function writeSet(key: string, set: Set<string>) {
      try {
        if (typeof localStorage === 'undefined') return
        localStorage.setItem(key, JSON.stringify([...set]))
      } catch {
        /* ignore quota/security errors */
      }
    }
    const expandedSubfolders = ref<Set<string>>(readSet(SIDEBAR_EXPANDED_KEY))

    function isSubfolderExpanded(sub: SubFolderMenuItem): boolean {
      return expandedSubfolders.value.has(sub.path)
    }

    function toggleSubfolder(sub: SubFolderMenuItem) {
      const next = new Set(expandedSubfolders.value)
      if (next.has(sub.path)) next.delete(sub.path)
      else next.add(sub.path)
      expandedSubfolders.value = next
      writeSet(SIDEBAR_EXPANDED_KEY, next)
    }

    const menuItems: MenuItems = [
      {
        name: 'Package Versions',
        path: '/packages',
        icon: 'PackageIcon'
      },
      {
        name: 'Versioning',
        type: 'folder',
        icon: 'FolderIcon',
        children: [
          {
            name: 'API Calls',
            path: '/request-filter',
            icon: 'FilterIcon'
          },
          {
            name: 'PubSub Events',
            path: '/workers-filter',
            icon: 'SettingsIcon'
          }
        ]
      },
      {
        name: 'IAM Configuration',
        path: '/iam-config',
        icon: 'SettingsIcon'
      },
      {
        name: 'Audit Logs',
        path: '/audit-logs',
        icon: 'FileIcon'
      },
      {
        name: 'Database',
        type: 'folder',
        icon: 'DatabaseIcon',
        children: [
          {
            name: 'Firestore (raw)',
            path: '/database/firestore',
            icon: 'FileIcon'
          },
          {
            name: 'Mongo (raw)',
            path: '/database/mongo',
            icon: 'ServerIcon'
          },
          { type: 'divider', name: 'ClickHouse' },
          {
            name: 'ClickHouse AI',
            type: 'subfolder',
            path: '/database/clickhouse-ai',
            icon: 'AiQueryIcon',
            beta: true,
            children: [
              {
                name: 'ClickHouse Chat',
                path: '/database/clickhouse-ai/chat',
                icon: 'AiQueryIcon'
              },
              {
                name: 'Workload Advisor',
                path: '/database/clickhouse-ai/workload',
                icon: 'AiQueryIcon'
              },
              {
                name: 'Admin',
                path: '/database/clickhouse-ai/admin',
                icon: 'SettingsIcon',
                adminOnly: true
              }
            ]
          },
          { type: 'divider', name: 'Mongo / Atlas' },
          {
            name: 'Atlas Intel',
            type: 'subfolder',
            path: '/database/atlas-intel',
            icon: 'DatabaseIcon',
            beta: true,
            children: [
              { name: 'Overview', path: '/database/atlas-intel', icon: 'FileIcon' },
              { name: 'Costs', path: '/database/atlas-intel/costs', icon: 'FileIcon' },
              { name: 'Storage', path: '/database/atlas-intel/storage', icon: 'FileIcon' },
              { name: 'Indexes', path: '/database/atlas-intel/indexes', icon: 'FileIcon' },
              { name: 'Ops & Performance', path: '/database/atlas-intel/ops', icon: 'FileIcon' },
              {
                name: 'Recommendations',
                path: '/database/atlas-intel/recommendations',
                icon: 'FileIcon'
              },
              { name: 'Health', path: '/database/atlas-intel/health', icon: 'FileIcon' },
              {
                name: 'Atlas AI Chat',
                path: '/database/atlas-intel/atlas-ai-history',
                icon: 'AiQueryIcon'
              },
              {
                name: 'Admin',
                path: '/database/atlas-intel/admin',
                icon: 'SettingsIcon',
                adminOnly: true
              }
            ]
          },
          {
            name: 'Firestore Enterprise (Mongo Compatible)',
            path: '/database/firestore-estimator',
            icon: 'DatabaseIcon',
            beta: true
          }
        ]
      },
      {
        name: 'API Dependency Graph',
        path: '/dependency-graph',
        icon: 'ActivityIcon'
      },
      {
        name: 'Quality Gates',
        type: 'folder',
        icon: 'CheckCircleIcon',
        children: [
          {
            name: 'PR Results',
            path: '/quality-gates',
            icon: 'FileIcon'
          },
          {
            name: 'Compliance',
            path: '/quality-gates/compliance',
            icon: 'ReportIcon'
          }
        ]
      },
      {
        name: 'Initiatives',
        path: '/initiatives',
        icon: 'ActivityIcon'
      },
      {
        name: 'Task Sentinel',
        path: '/task-sentinel',
        icon: 'UsersIcon',
        adminOnly: true
      },
      {
        name: 'Platform Reports',
        path: '/platform-report',
        icon: 'ReportIcon',
        adminOnly: true
      }
    ]

    const isActive = (path: string) => route.path === path || route.path.startsWith(`${path}/`)

    // Get the logged-in user
    const user = computed(() => auth.getUser())

    // Check if current user is admin
    const userIsAdmin = computed(() => isAdmin(user.value))

    // Filter menu items based on admin status. Walks two levels deep: folder
    // children, plus subfolder grandchildren (the new locked sidebar nests
    // Atlas Intel and ClickHouse AI as subfolders containing leaf children).
    const filteredMenuItems = computed(() => {
      return menuItems
        .filter((item) => !item.adminOnly || userIsAdmin.value)
        .map((item) => {
          if ((item as FolderMenuItem).type === 'folder') {
            const folder = item as FolderMenuItem
            const filteredChildren = folder.children
              .filter((c) => {
                const adminOnly = (c as RegularMenuItem | SubFolderMenuItem | DividerMenuItem)
                  .adminOnly
                return !adminOnly || userIsAdmin.value
              })
              .map((c) => {
                if ((c as SubFolderMenuItem).type === 'subfolder') {
                  const sub = c as SubFolderMenuItem
                  return {
                    ...sub,
                    children: sub.children.filter((g) => !g.adminOnly || userIsAdmin.value)
                  }
                }
                return c
              })
            return { ...folder, children: filteredChildren }
          }
          return item
        })
    })

    // Logout handler
    const handleLogout = () => {
      auth.logout()
    }

    const getUserInitials = computed(() => {
      if (user.value && user.value.name) {
        const initials = user.value.name
          .split(' ')
          .map((word) => word[0])
          .join('')
        return initials.toUpperCase()
      }
      return ''
    })

    const getUserColor = computed(() => {
      if (user.value && user.value.name) {
        const colors = ['#FF5757', '#FFB800', '#00D084', '#0083FF', '#7A5AF8']
        const index =
          user.value.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
          colors.length
        return colors[index]
      }
      return '#ccc'
    })

    return {
      menuItems: filteredMenuItems,
      isActive,
      route,
      user,
      handleLogout,
      getUserInitials,
      getUserColor,
      theme,
      isSubfolderExpanded,
      toggleSubfolder
    }
  }
})
</script>

<style scoped>
.router-link-active:not(.logo-link) {
  @apply bg-gray-200 dark:bg-gray-800;
  font-size: 1rem;
}

.router-link {
  font-size: 1rem;
  @apply text-gray-700 dark:text-gray-300;
}

.flex.items-center {
  font-size: 1rem;
}

.logo-link.router-link-active {
  background-color: transparent !important;
}
</style>
